import React, { useEffect, useState, useMemo } from 'react';
import { Box,
    Typography,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel } from '@mui/material';
import { CustomButton } from '../../../shared';
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon } from '../../../../assets';
import { useDispatch, useSelector } from 'react-redux';
import TableRowsSkeleton from '../../../shared/skelton/tableSkelton';
import { CSVLink } from 'react-csv';
import { actions as triggerActions } from '../../../../redux/reducers/triggerReducer';
import { calculateMaxWidth,
    getSortedData,
    handleRowClassForEngagementsTable } from '../../../../utility';
import { getWebMatrixPatientAnalysisSummaryTrigger, getWebMatrixPatientTrigger } from '../../../../redux/thunks/triggerThunk';
import { useParams } from 'react-router-dom';
import { getEngagementRateDataFalse } from '../../../../redux/thunks/webMatrixThunks';

const DtcTriggerTable = () => {
    const dispatch = useDispatch();
    const { trigger } = useParams();
    const { dtcData,isTriggerLoading } = useSelector((state) => state.trigger);
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);
    const {
        dtcTableData = [],
        filteredDtcTableData = [],
        isWebMatrixPatientSummaryLoading = false,
        currentPage = 1,
        pageSize = 10,
        totalPages = 0,
        totalRecordsFetch = 0,
        isWebMatrixPatientLoading
    } = dtcData;

    const { setDtcTableCurrentPage,resetDtcTableStates } = triggerActions;
    const { triggerDetails: { name: triggerName } = {} } = useSelector((state) => state.trigger);

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [maxWidth, setMaxWidth] = useState(calculateMaxWidth());
    const [filteredData, setFilteredData] = useState([]);

    const isLoading = (isWebMatrixPatientSummaryLoading);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const sortedData = useMemo(
        () => getSortedData(orderBy, order, filteredDtcTableData),
        [order, orderBy, filteredDtcTableData]
    );

    const handlePageChange = (event, page) => {
        dispatch(setDtcTableCurrentPage(page));
    };

    const csvData = useMemo(() => dtcTableData.map((provider) => ({
        date: provider?.date || '',
        audience_type: provider?.audience_type || '',
        creative_asset: provider?.creative_asset || '',
        site_name: provider?.site_name || '',
        channel: provider?.channel || '',
        vendor: provider?.vendor || '',
        impressions: provider?.impressions || 0,
        clicks: provider?.clicks || 0
    })), [dtcTableData]);

    const csvHeaders = useMemo(
        () => [
            { label: 'Date', key: 'date' },
            { label: 'Audience Type', key: 'audience_type' },
            // { label: 'Site Name', key: 'site_name' },
            { label: 'Creative Asset', key: 'creative_asset' },
            { label: 'Channel', key: 'channel' },
            { label: 'Vendor', key: 'vendor' },
            { label: 'Impressions', key: 'impressions' },
            { label: 'Clicks', key: 'clicks' }
        ],
        []
    );

    const renderTableData = () => {
        if (totalRecordsFetch === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={8}>
                        <Box p={3} textAlign="center">
                            <Typography className="text-sm-regular gray-900">
                                No data to display.
                            </Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }

        return sortedData.map((row, index) => (
            <TableRow
                className={handleRowClassForEngagementsTable(index, company?.id)}
                key={index}
            >
                <TableCell className="table-row-data no-border wh_space_overflow_h_text_of">{row?.date}</TableCell>
                <TableCell className="table-row-data no-border">{row?.audience_type}</TableCell>
                {/* <TableCell className="table-row-data no-border">{row?.site_name}</TableCell> */}
                <TableCell className="table-row-data no-border">{row?.creative_asset}</TableCell>
                <TableCell className="table-row-data no-border">{row?.channel}</TableCell>
                <TableCell className="table-row-data no-border">{row?.vendor}</TableCell>
                <TableCell className="table-row-data no-border">{row?.impressions}</TableCell>
                <TableCell className="table-row-data no-border">{row?.clicks}</TableCell>
            </TableRow>
        ));
    };
    useEffect(() => {
        dispatch(resetDtcTableStates());
        if (company.id) {
            dispatch(
                getWebMatrixPatientAnalysisSummaryTrigger({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                    page_no: currentPage,
                    page_size: 20,
                    triggerName: trigger
                })
            );
        }
    }, [company, startDateRange, endDateRange,currentPage]);
    useEffect(() => {
        if (company?.id) {
            dispatch(
                getWebMatrixPatientTrigger({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                    triggerName: trigger
                })
            );
            dispatch(
                getEngagementRateDataFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                    trigger_name: trigger
                })
            );
        }
    }, [company?.id, startDateRange, endDateRange]);

    useEffect(() => {
        const handleResize = () => setMaxWidth(calculateMaxWidth());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <Box className="b1px-border-box-br8px" sx={{ maxWidth: maxWidth, width: '100%', minWidth: 0 }}>
            <Box className="padding-tb-20-rl-24-bb-1px">
                <Box className="flex-row-space-between-center">
                    <Typography className="text-md-medium gray-900">See Full List Below</Typography>
                    {/* this need to be fixed by api then we will add this functionality */}
                    {/* <CSVLink data={csvData} filename={triggerName || 'DTC_Trigger_Data.csv'} headers={csvHeaders}>
                        <CustomButton
                            buttonColor="#AD509E"
                            icon={<DownloadIcon />}
                            title="Export All Data"
                            titleColor="#FFFFFF"
                        />
                    </CSVLink> */}
                </Box>
            </Box>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer className="no-box-shadow no-border" sx={{ overflowX: 'auto' }} component={Paper}>
                    <Table className="table-border-collapse no-border">
                        <TableHead>
                            <TableRow>
                                {csvHeaders.map(({ label, key }) => (
                                    <TableCell className="table-headers-title" key={key}>
                                        <TableSortLabel
                                            active={orderBy === key}
                                            direction={orderBy === key ? order : 'asc'}
                                            onClick={() => handleRequestSort(key)}
                                        >
                                            {label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRowsSkeleton columns={7} />
                            ) : (
                                renderTableData()
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {totalPages > 0 && (
                <Box className="padding-tb-16-rl-24 border-top-gray">
                    <Box className="flex-row-space-between-center">
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={currentPage === 1}
                            handleClick={() => handlePageChange(null, currentPage - 1)}
                            icon={<ArrowLeftIcon />}
                            title="Previous"
                            titleColor="#344054"
                        />
                        <Pagination
                            hideNextButton
                            hidePrevButton
                            count={totalPages}
                            page={currentPage}
                            shape="rounded"
                            onChange={handlePageChange}
                        />
                        <CustomButton
                            buttonColor="#FFFFFF"
                            buttonDisabled={currentPage === totalPages}
                            handleClick={() => handlePageChange(null, currentPage + 1)}
                            tailIcon={<ArrowRightIcon />}
                            title="Next"
                            titleColor="#344054"
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DtcTriggerTable;
